import React, { useState, useEffect, useRef } from 'react';
import { 
  MagnifyingGlassIcon,
  UserIcon,
  UserGroupIcon,
  StarIcon,
  BuildingStorefrontIcon,
  GlobeAsiaAustraliaIcon,
  BuildingOfficeIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { searchUsers, getAvailableCurrencies, processTransaction, forceReloadUsers, getUsersData, buildTree } from '../data/users';
import { fetchAuthSession } from 'aws-amplify/auth';
import { notificationService } from '../services/notificationService';
import { getDirectReports } from '../services/userSelf';
import useTranslation from '../services/translationService';
import { apiPost } from '../utils/api';
import { isBreakglassModeActive } from '../services/breakglassService';

const getUserIcon = (type) => {
  switch (type) {
    case 'admin':
      return BuildingOfficeIcon;
    case 'country':
      return GlobeAsiaAustraliaIcon;
    case 'city':
      return BuildingStorefrontIcon;
    case 'super-agent':
      return StarIcon;
    case 'agent':
      return UserGroupIcon;
    case 'player':
      return UserIcon;
    default:
      return UserIcon;
  }
};

const getUserStyle = (type) => {
  switch (type) {
    case 'admin':
      return 'text-purple-500';
    case 'country':
      return 'text-blue-500';
    case 'city':
      return 'text-green-500';
    case 'super-agent':
      return 'text-orange-500';
    case 'agent':
      return 'text-yellow-500';
    case 'player':
      return 'text-gray-500';
    default:
      return 'text-gray-400';
  }
};

const TransactionModal = ({ isOpen, onClose, onSubmit, type = 'deposit', preselectedUser = null }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [transactionType, setTransactionType] = useState(type);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentUserType, setCurrentUserType] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [allSubordinates, setAllSubordinates] = useState([]);
  const [userWalletBalances, setUserWalletBalances] = useState({});
  const [isTestTransaction, setIsTestTransaction] = useState(false);
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const [isBreakglassActive, setIsBreakglassActive] = useState(false);

  // Check for breakglass mode
  useEffect(() => {
    const checkBreakglass = async () => {
      const breakglassActive = await isBreakglassModeActive();
      setIsBreakglassActive(breakglassActive);
    };
    
    if (isOpen) {
      checkBreakglass();
    }
  }, [isOpen]);

  // Function to fetch wallet balances from the API
  const fetchWalletBalances = async (username) => {
    try {
      // Validate username parameter
      if (!username) {
        console.error('No username provided to fetchWalletBalances');
        return selectedUser?.balances || {};
      }
      
      console.log('Fetching wallet balances for:', username);
      
      // Get the current session token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        console.error('No authentication token available');
        return selectedUser?.balances || {};
      }
      
      // Build request body
      const requestBody = {
        action: 'getBalances',
        username: username
      };
      
      console.log('Sending balance request with body:', requestBody);
      
      // Use apiPost with the action parameter
      const response = await apiPost(
        'coretransact',
        '/core/transact',
        requestBody,
        token
      );
      
      console.log('Fetched wallet balances response:', response);
      
      if (response && response.balances) {
        setUserWalletBalances(response.balances);
        return response.balances;
      } else if (response && response.success) {
        // If successful but no balances, return empty object
        console.log('User has no wallet entries yet');
        setUserWalletBalances({});
        return {};
      }
      
      // If response has error details, log them
      if (response && response.details) {
        console.error('Balance fetch error details:', response.details);
      }
      
      return {};
    } catch (error) {
      console.error('Error fetching wallet balances:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      
      // Fallback to user's cached balances if API fails
      const fallbackBalances = selectedUser?.balances || preselectedUser?.balances || {};
      console.log('Using fallback balances:', fallbackBalances);
      return fallbackBalances;
    }
  };

  // Add function to determine valid transaction targets
  const getValidTargetType = (userType) => {
    switch (userType) {
      case 'admin':
        return ['admin', 'country', 'city', 'super-agent', 'agent', 'player']; // Admins can transact with anyone
      case "country":
        return ['country', 'city', 'super-agent', 'agent', 'player'];
      case 'city':
        return ['super-agent', 'agent', 'player']; // City can transact with anyone below in hierarchy
      case 'super-agent':
        return ['agent', 'player']; // Super-agent can transact with direct reports only (agents and players)
      case 'agent':
        return 'player';
      default:
        return null;
    }
  };

  // Function to get all subordinates recursively
  const getAllSubordinates = (node, subordinates = [], directOnly = false) => {
    if (!node || !node.children) return subordinates;
    
    node.children.forEach(child => {
      // Add child to subordinates if they have balances
      if (child.balances) {
        subordinates.push(child);
      }
      // Only recurse if not directOnly mode
      if (!directOnly) {
        // Recursively get child's subordinates
        getAllSubordinates(child, subordinates, directOnly);
      }
    });
    
    return subordinates;
  };

  // Add effect to get current user's type and subordinates
  useEffect(() => {
    const getCurrentUserType = async () => {
      try {
        const session = await fetchAuthSession();
        const { success, data } = await getUsersData();
        
        console.log("Auth session:", session);
        console.log("Users data success:", success);
        
        if (success && data) {
          // Get the username from the session
          const username = session.tokens?.accessToken?.payload?.username || 
                          session.tokens?.idToken?.payload?.['cognito:username'] || 
                          null;
          
          console.log("Current username:", username);
          console.log("Available users:", Object.values(data.users).map(u => u.username));
          
          if (username) {
            const currentUser = Object.values(data.users).find(u => 
              u.username === username || 
              u.username.toLowerCase() === username.toLowerCase()
            );
            
            console.log("Found current user:", currentUser);
            
            if (currentUser) {
              setCurrentUserType(currentUser.type);
              setIsCurrentUserAdmin(currentUser.type === 'admin');
              
              // For super-agents and agents, build tree and get subordinates
              if (currentUser.type === 'super-agent' || currentUser.type === 'agent') {
                try {
                  const tree = await buildTree(data, data.rootId);
                  
                  // Find current user in the tree
                  const findUserInTree = (node, targetUsername) => {
                    if (node.username === targetUsername) return node;
                    if (node.children) {
                      for (const child of node.children) {
                        const found = findUserInTree(child, targetUsername);
                        if (found) return found;
                      }
                    }
                    return null;
                  };
                  
                  const currentUserNode = findUserInTree(tree, username);
                  if (currentUserNode) {
                    // Super-agents: only direct reports (directOnly = true)
                    // Agents: all subordinates (directOnly = false)
                    const directOnly = currentUser.type === 'super-agent';
                    const subordinates = getAllSubordinates(currentUserNode, [], directOnly);
                    setAllSubordinates(subordinates);
                    console.log("Loaded subordinates for", currentUser.type, "(direct only:", directOnly, "):", subordinates.length);
                  }
                } catch (error) {
                  console.error("Error building tree for subordinates:", error);
                  setAllSubordinates([]);
                }
              } else {
                setAllSubordinates([]);
              }
            } else {
              console.error("Could not find current user in users data");
              // Default to admin to allow transactions while debugging
              setCurrentUserType('admin');
              setAllSubordinates([]);
              console.log("Defaulted to admin type");
            }
          } else {
            console.error("Could not extract username from session");
            // Default to admin to allow transactions while debugging
            setCurrentUserType('admin');
            setAllSubordinates([]);
            console.log("Defaulted to admin type");
          }
        } else {
          console.error("Failed to get users data");
          // Default to admin to allow transactions while debugging
          setCurrentUserType('admin');
          setAllSubordinates([]);
          console.log("Defaulted to admin type");
        }
      } catch (error) {
        console.error('Error getting current user type:', error);
        // Default to admin to allow transactions while debugging
        setCurrentUserType('admin');
        setAllSubordinates([]);
        console.log("Defaulted to admin type due to error");
      }
    };

    if (isOpen) {
      getCurrentUserType();
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchUsers = async () => {
      // For super-agents and agents, show all subordinates when search is focused (empty or short query)
      if ((currentUserType === 'super-agent' || currentUserType === 'agent') && 
          (!searchQuery || searchQuery.length < 2) && 
          allSubordinates.length > 0) {
        
        console.log('Showing all subordinates for', currentUserType, ':', allSubordinates.length);
        // Sort by username length (shortest first)
        const sortedSubordinates = [...allSubordinates].sort((a, b) => {
          const usernameA = a.username || '';
          const usernameB = b.username || '';
          return usernameA.length - usernameB.length;
        });
        setFilteredUsers(sortedSubordinates.map(user => ({
          id: user.id,
          name: user.name,
          username: user.username,
          type: user.type,
          balances: user.balances || {},
          children: user.children,
          balance: new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(user.balances?.[currency] || 0)
        })));
        return;
      }
      
      if (searchQuery && searchQuery.length >= 2) {
        let results;
        
        // For super-agents and agents, search within subordinates first, then fallback to global search
        if ((currentUserType === 'super-agent' || currentUserType === 'agent') && allSubordinates.length > 0) {
          const searchTerm = searchQuery.toLowerCase();
          const subordinateResults = allSubordinates.filter(user => 
            user.name.toLowerCase().includes(searchTerm) || 
            user.username.toLowerCase().includes(searchTerm)
          );
          
          // If we found matches in subordinates, use those. Otherwise, do global search
          if (subordinateResults.length > 0) {
            results = subordinateResults;
            console.log('Found subordinate matches:', subordinateResults.length);
          } else {
            results = await searchUsers(searchQuery);
            console.log('No subordinate matches, using global search results:', results.length);
          }
        } else {
          results = await searchUsers(searchQuery);
          console.log('Global search results:', results.length);
        }
        
        // Filter users only if currentUserType exists
        if (currentUserType) {
          const validTargetType = getValidTargetType(currentUserType);
          console.log('Current user type:', currentUserType); 
          console.log('Valid target types:', validTargetType);
          
          // Get direct reports if super-agent
          let directReports = [];
          if (currentUserType === 'super-agent') {
            directReports = await getDirectReports();
            console.log('Direct reports:', directReports);
          }
          
          // Modified filtering logic to handle array of valid types and direct reports
          let filteredResults = results;
          
          // Only apply type filtering for non-admin users
          if (currentUserType !== 'admin') {
            filteredResults = results.filter(user => {
              // Check if user has balances
              if (!user.balances) {
                return false;
              }
              
              // Include user if they are a subordinate
              if (allSubordinates.some(sub => sub.username === user.username)) {
                return true;
              }
              
              // Include user if they are a direct report
              if (directReports.includes(user.username)) {
                return true;
              }
              
              // Handle both array and string valid types
              if (Array.isArray(validTargetType)) {
                return validTargetType.includes(user.type);
              } else {
                return user.type === validTargetType;
              }
            });
          }
          
          console.log('Filtered results:', filteredResults);
          
          // Sort by username length (shortest first)
          const sortedResults = filteredResults.sort((a, b) => {
            const usernameA = a.username || '';
            const usernameB = b.username || '';
            return usernameA.length - usernameB.length;
          });
          
          setFilteredUsers(sortedResults.map(user => ({
            id: user.id,
            name: user.name,
            username: user.username,
            type: user.type,
            balances: user.balances || {},
            children: user.children,
            balance: new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: currency,
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(user.balances?.[currency] || 0)
          })));
        } else {
          // If currentUserType isn't available yet, show all users
          console.log('No currentUserType yet, showing all users');
          // Sort by username length (shortest first)
          const sortedResults = results.sort((a, b) => {
            const usernameA = a.username || '';
            const usernameB = b.username || '';
            return usernameA.length - usernameB.length;
          });
          setFilteredUsers(sortedResults.map(user => ({
            id: user.id,
            name: user.name,
            username: user.username,
            type: user.type,
            balances: user.balances || {},
            children: user.children,
            balance: new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: currency,
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(user.balances?.[currency] || 0)
          })));
        }
      } else {
        setFilteredUsers([]);
      }
    };
    
    // Only fetch users if we're not showing a preselected user
    if (!selectedUser) {
      fetchUsers();
    }
  }, [searchQuery, currency, currentUserType, selectedUser, allSubordinates]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      // Only fetch all currencies when no user is selected
      if (!selectedUser) {
        const currencies = await getAvailableCurrencies();
        setAvailableCurrencies(currencies);
        if (currencies.length > 0 && !currencies.includes('USD')) {
          setCurrency(currencies[0]);
        }
      }
    };
    
    fetchCurrencies();
  }, [selectedUser]);

  // Initial load effect - no longer needed as we fetch fresh balances directly
  useEffect(() => {
    if (isOpen) {
      console.log('Transaction modal opened - will fetch fresh balances for selected user');
    }
  }, [isOpen]); // Only run when modal opens/closes

  // Effect to handle preselected user and background refresh
  useEffect(() => {
    if (isOpen && preselectedUser) {
      // Immediately set the preselected user for instant UI feedback
      setSelectedUser(preselectedUser);
      setSearchQuery(preselectedUser.name || '');

      // Fetch fresh wallet balances
      const fetchFreshBalances = async () => {
        // Validate preselectedUser has a username
        if (!preselectedUser.username) {
          console.warn('Preselected user has no username:', preselectedUser);
          return;
        }
        
        setIsRefreshing(true);
        try {
          // Fetch wallet balances from the new endpoint
          const freshBalances = await fetchWalletBalances(preselectedUser.username);
          
          if (freshBalances && Object.keys(freshBalances).length > 0) {
            // Update available currencies based on fresh balances
            const userCurrencies = Object.keys(freshBalances);
            setAvailableCurrencies(userCurrencies);
            
            if (!userCurrencies.includes(currency)) {
              setCurrency(userCurrencies[0]);
            }
            
            // Update the selected user with fresh balances
            setSelectedUser(prevUser => ({
              ...prevUser,
              balances: freshBalances
            }));
          } else {
            // Fallback to preselected user's balances
            if (preselectedUser.balances) {
              const userCurrencies = Object.keys(preselectedUser.balances);
              if (userCurrencies.length > 0) {
                setAvailableCurrencies(userCurrencies);
                if (!userCurrencies.includes(currency)) {
                  setCurrency(userCurrencies[0]);
                }
              }
              setUserWalletBalances(preselectedUser.balances);
            }
          }
        } catch (error) {
          console.error('Error fetching fresh balances:', error);
          // Keep using the preselected user data if fetch fails
          if (preselectedUser.balances) {
            const userCurrencies = Object.keys(preselectedUser.balances);
            if (userCurrencies.length > 0) {
              setAvailableCurrencies(userCurrencies);
              if (!userCurrencies.includes(currency)) {
                setCurrency(userCurrencies[0]);
              }
            }
            setUserWalletBalances(preselectedUser.balances || {});
          }
        } finally {
          setIsRefreshing(false);
        }
      };

      fetchFreshBalances();
    }
  }, [isOpen, preselectedUser]);

  // When modal opens or closes - reset when opening but not when closing with success
  useEffect(() => {
    if (isOpen) {
      // When opening, initialize based on preselected user but don't clear success message
      if (!successMessage) {
        setSelectedUser(preselectedUser || null);
        setSearchQuery(preselectedUser?.name || '');
        setTransactionType(type);
      }
    } else {
      // When closing, do a complete reset if we were showing a receipt
      if (successMessage) {
        setError(null);
        setSuccessMessage(null);
        setAmount('');
        setNotes('');
        setShowNotes(false);
        setSearchQuery('');
        setSelectedUser(null);
        setUserWalletBalances({});
        setTransactionType(type);
        setIsTestTransaction(false);
      }
    }
  }, [isOpen, preselectedUser, type, successMessage]);

  const handleUserSelect = async (user) => {
    // Validate user has a username
    if (!user || !user.username) {
      console.error('Invalid user selected:', user);
      return;
    }
    
    setSelectedUser(user);
    setSearchQuery('');
    setFilteredUsers([]);
    setIsRefreshing(true);
    
    // Fetch fresh wallet balances for the selected user
    try {
      const freshBalances = await fetchWalletBalances(user.username);
      
      // Update available currencies based on fresh balances
      if (freshBalances && Object.keys(freshBalances).length > 0) {
        const userCurrencies = Object.keys(freshBalances);
        setAvailableCurrencies(userCurrencies);
        
        // If current currency is not available for this user, set to first available
        if (!userCurrencies.includes(currency)) {
          setCurrency(userCurrencies[0]);
        }
        
        // Update the selected user with fresh balances
        setSelectedUser(prevUser => ({
          ...prevUser,
          balances: freshBalances
        }));
      } else {
        // Fallback to cached balances if no fresh data
        if (user.balances) {
          const userCurrencies = Object.keys(user.balances);
          if (userCurrencies.length > 0) {
            setAvailableCurrencies(userCurrencies);
            if (!userCurrencies.includes(currency)) {
              setCurrency(userCurrencies[0]);
            }
          }
        }
        setUserWalletBalances(user.balances || {});
      }
    } catch (error) {
      console.error('Error in handleUserSelect:', error);
      // Fallback to cached data
      if (user.balances) {
        const userCurrencies = Object.keys(user.balances);
        if (userCurrencies.length > 0) {
          setAvailableCurrencies(userCurrencies);
          if (!userCurrencies.includes(currency)) {
            setCurrency(userCurrencies[0]);
          }
        }
        setUserWalletBalances(user.balances || {});
      }
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const handleRemoveUser = () => {
    setSelectedUser(null);
    setSearchQuery('');
    setUserWalletBalances({});
    // Reset to all available currencies
    fetchCurrencies();
  };
  
  const fetchCurrencies = async () => {
    const currencies = await getAvailableCurrencies();
    setAvailableCurrencies(currencies);
    if (currencies.length > 0 && !currencies.includes('USD')) {
      setCurrency(currencies[0]);
    }
  };

  const formatTransactionDetails = (result, isDeposit) => {
    const type = isDeposit ? t('transactions.deposit', 'Deposit') : t('transactions.withdraw', 'Withdrawal');
    const sign = isDeposit ? '+' : '-';
    return (
      <div className="flex flex-col">
        <div className="text-emerald-600 dark:text-emerald-400 font-medium mb-4">
          {result.message}
        </div>
        {result.isTestTransaction && (
          <div className="mb-3 p-2 bg-purple-100 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-700 rounded-md flex items-center gap-2">
            <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
              {t('transactions.testTransactionLabel', 'Test Transaction')}
            </span>
          </div>
        )}
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400 text-xs">{t('transactions.type', 'Transaction Type')}</span>
              <span className="font-medium">{type}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400 text-xs">{t('transactions.amount', 'Amount')}</span>
              <span className={`font-medium ${isDeposit ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                {sign}{formatBalance(result.amount, result.currency)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400 text-xs">{t('transactions.from', 'From')}</span>
              <span className="font-medium">@{result.initiator}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400 text-xs">{t('transactions.to', 'To')}</span>
              <span className="font-medium">@{result.targetAgent}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400 text-xs">{t('transactions.transactionId', 'Transaction ID')}</span>
              <span className="font-medium text-xs break-all">{result.transactionId}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400 text-xs">{t('transactions.time', 'Time')}</span>
              <span className="font-medium">{new Date(result.timestamp).toLocaleString()}</span>
            </div>
          </div>

          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
              {t('transactions.balanceChanges', 'Balance Changes')}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{t('transactions.previousBalance', 'Previous Balance')}:</span>
                <span className="font-medium">{formatBalance(result.targetBalance, result.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{t('transactions.newBalance', 'New Balance')}:</span>
                <span className="font-medium">
                  {formatBalance(result.newTargetBalance, result.currency)}
                </span>
              </div>
              <div className="h-px bg-gray-200 dark:bg-gray-600 my-1"></div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{t('transactions.change', 'Change')}:</span>
                <span className={`font-medium ${isDeposit ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {isDeposit ? '+' : '-'}{formatBalance(result.amount, result.currency)}
                </span>
              </div>
            </div>
          </div>

          {result.notes && (
            <div className="flex flex-col">
              <span className="text-gray-500 dark:text-gray-400 text-xs">{t('transactions.notes', 'Notes')}</span>
              <span className="font-medium mt-1 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                {result.notes}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const formatNumberWithCommas = (value) => {
    // If it's empty or just a ".", return as-is to allow partial input
    if (value === '' || value === '.') return value;

    // Remove all non-digit characters except the decimal point
    const numericValue = value.replace(/[^\d.]/g, '');

    // Split by ".", to handle integer vs. decimal
    const parts = numericValue.split('.');
    let wholePart = parts[0]; // Everything before the decimal
    const decimalPart = parts[1] ? `.${parts[1]}` : ''; // Keep decimal if present

    // Add commas to the whole part
    wholePart = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Recombine
    return wholePart + decimalPart;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    
    // If the value is just a dot, allow it
    if (value === '.') {
      setAmount(value);
      return;
    }
    
    // If there's already a dot in the value, preserve everything after it
    const dotIndex = value.indexOf('.');
    if (dotIndex !== -1) {
      const beforeDot = value.substring(0, dotIndex).replace(/\D/g, '');
      const afterDot = value.substring(dotIndex);
      const formattedBeforeDot = beforeDot.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      setAmount(formattedBeforeDot + afterDot);
      return;
    }
    
    // For numbers without dots, just format with commas
    const cleanValue = value.replace(/\D/g, '');
    const formattedValue = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setAmount(formattedValue);
  };

  const handleAmountBlur = (e) => {
    const value = e.target.value;
    if (value !== '') {
      const formattedValue = formatNumberWithCommas(value);
      setAmount(formattedValue);
    }
  };

  // Debug function to check button state
  const getButtonDisabledReason = () => {
    if (isSubmitting) return "Submitting in progress";
    if (!selectedUser) return "No user selected";
    if (!amount) return "No amount entered";
    if (!currency) return "No currency selected";
    
    const numericAmount = parseFloat(amount.replace(/,/g, ''));
    if (isNaN(numericAmount) || numericAmount <= 0) return "Invalid amount";
    
    if (transactionType === 'withdrawal' && selectedUser && currency) {
      const currentBalance = selectedUser.balances?.[currency] || userWalletBalances[currency] || 0;
      if (numericAmount > currentBalance) return `Insufficient balance (${formatBalance(currentBalance, currency)} available)`;
    }
    
    if (!isValidTarget(currentUserType, selectedUser?.type)) {
      return `Invalid target type: ${selectedUser?.type} (expected ${JSON.stringify(getValidTargetType(currentUserType))})`;
    }
    
    return null; // Not disabled
  };
  
  useEffect(() => {
    console.log("Button disabled reason:", getButtonDisabledReason());
  }, [isSubmitting, selectedUser, amount, currency, currentUserType]);

  // Improved validation function with better null handling
  const isValidTarget = async (userType, targetType) => {
    if (!userType || !targetType) return true;
    
    const validTypes = getValidTargetType(userType);
    if (!validTypes) return false;
    
    // First check: Is the type in the valid types list?
    const isValidType = Array.isArray(validTypes) 
      ? validTypes.includes(targetType)
      : targetType === validTypes;

    if (isValidType) return true;

    // Second check: Is the target user a direct report?
    try {
      const directReports = await getDirectReports();
      return directReports.includes(selectedUser.username);
    } catch (error) {
      console.error("Error checking direct reports:", error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedUser || !isAmountValid() || !currency) {
      setError('Please fill in all required fields properly');
      console.log("Submission failed: invalid fields");
      return;
    }

    // Check if this is a valid transaction target
    const isValid = await isValidTarget(currentUserType, selectedUser.type);
    if (!isValid) {
      setError('Invalid target: Must be either a valid type or your direct report');
      console.log("Submission failed: invalid target type or not a direct report");
      return;
    }
    
    // Remove commas before converting to number
    const numAmount = parseFloat(amount.replace(/,/g, ''));
    console.log("Parsed amount:", numAmount);
    
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Amount must be a positive number');
      console.log("Submission failed: invalid amount value");
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const isDeposit = transactionType === 'deposit';
      
      const result = await processTransaction(
        selectedUser,
        currency,
        numAmount,
        isDeposit,
        notes,
        isTestTransaction
      );
      
      // Log the raw response for debugging
      console.log('Raw transaction response:', result);
      
      // Check if the response contains an error message
      if (result.error) {
        console.log('Transaction result contained error:', result);
        throw result; // Throw the entire result object
      }

      // Log the successful response
      console.log('Transaction successful - Response data:', result);

      // Send notification to the target user
      try {
        const targetUsername = selectedUser.username;
        const transactionSign = isDeposit ? '+' : '-';
        const amountFormatted = formatBalance(numAmount, currency);
        const transactionType = isDeposit ? 'deposit' : 'withdrawal';
        
        // Send notification with transaction details
        await notificationService.sendNotification({
          content: `Transaction: ${transactionSign}${amountFormatted} ${transactionType} - Your balance has been updated`,
          recipients: [targetUsername]
        });
        
        console.log('Transaction notification sent to:', targetUsername);
      } catch (notifyError) {
        console.error('Failed to send transaction notification:', notifyError);
        // Don't fail the transaction if notification fails
      }

      // Store both the raw result and set the success message
      setSuccessMessage({
        details: result,
        isDeposit
      });
      
      // Update the selected user's balance immediately for UI feedback only
      const updatedUser = {
        ...selectedUser,
        balances: {
          ...selectedUser.balances,
          [currency]: isDeposit 
            ? (selectedUser.balances[currency] || 0) + numAmount
            : (selectedUser.balances[currency] || 0) - numAmount
        }
      };
      setSelectedUser(updatedUser);
      
      // Clear the amount field after success
      setAmount('');
      
      // Schedule a refresh after 2 seconds to match wallet cache TTL
      setTimeout(() => {
        forceReloadUsers().catch(e => {
          console.warn('Failed to reload users after transaction delay:', e);
        });
      }, 100);
      
      // Just notify parent of success - don't close or refresh
      if (onSubmit) {
        onSubmit({ 
          success: true,
          user: updatedUser,
          transactionDetails: result,
          shouldRefreshTree: false // No immediate refresh, using delayed refresh instead
        });
      }
      
    } catch (error) {
      // Add detailed error logging
      console.error('Transaction error - Full error object:', error);
      
      // Handle both string errors and error objects
      const errorMessage = error.error || error.message || 'Failed to process transaction';
      console.error('Final error message being set:', errorMessage);
      setError(errorMessage);
      
      // Reset submission state on error
      setIsSubmitting(false);
      return;
    }
    
    setIsSubmitting(false);
  };

  // Handle modal close
  const handleClose = () => {
    // Only reset if not showing receipt (receipt cleanup happens in useEffect)
    if (!successMessage) {
      setError(null);
      setAmount('');
      setNotes('');
      setShowNotes(false);
      setSearchQuery('');
      setSelectedUser(null);
      setUserWalletBalances({});
      setTransactionType(type);
      setIsTestTransaction(false);
    }
    
    // Close the modal
    onClose();
  };

  // Prevent any accidental backdrop closing when showing receipt
  const handleBackdropClick = (e) => {
    // Only close if clicked on backdrop directly
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const getTransactionColor = () => {
    return transactionType === 'deposit' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  };

  const getTransactionBgColor = () => {
    return transactionType === 'deposit' ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-red-50 dark:bg-red-900/20';
  };

  const formatBalance = (amount, currency) => {
    if (!currency) {
      // If no currency is provided, just format the number with commas
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: String(currency).toUpperCase() === 'KWD' ? 3 : 2,
    }).format(amount || 0);
  };

  // Add a logging function for selectedUser
  const logSelectedUser = () => {
    try {
      console.log("Selected user object:", selectedUser);
      console.log("Selected user type:", selectedUser?.type);
      console.log("Selected user balances:", selectedUser?.balances);
      console.log("Selected user name:", selectedUser?.name);
    } catch (e) {
      console.error("Error logging selected user:", e);
    }
  };

  // Check if amount is valid (non-empty and > 0)
  const isAmountValid = () => {
    if (!amount) return false;
    const numericAmount = parseFloat(amount.replace(/,/g, ''));
    if (isNaN(numericAmount) || numericAmount <= 0) return false;
    
    // For withdrawals, check if there's sufficient balance
    if (transactionType === 'withdrawal' && selectedUser && currency) {
      const currentBalance = selectedUser.balances?.[currency] || userWalletBalances[currency] || 0;
      return numericAmount <= currentBalance;
    }
    
    return true;
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value === '') {
      setSelectedUser(preselectedUser || null);
    } else if (selectedUser) {
      setSelectedUser(null);
    }
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    // For super-agents and agents, show all subordinates when focused
    if ((currentUserType === 'super-agent' || currentUserType === 'agent') && !searchQuery && allSubordinates.length > 0) {
      // Sort by username length (shortest first)
      const sortedSubordinates = [...allSubordinates].sort((a, b) => {
        const usernameA = a.username || '';
        const usernameB = b.username || '';
        return usernameA.length - usernameB.length;
      });
      setFilteredUsers(sortedSubordinates.map(user => ({
        id: user.id,
        name: user.name,
        username: user.username,
        type: user.type,
        balances: user.balances || {},
        children: user.children,
        balance: new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(user.balances?.[currency] || 0)
      })));
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[150]" 
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-11/12 max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {successMessage ? t('transactions.complete', 'Transaction Complete') : t('transactions.title', 'Transaction')}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
              <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMessage ? (
            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircleIcon className="w-6 h-6 text-emerald-500 dark:text-emerald-400" />
                <span className="text-lg font-semibold text-emerald-700 dark:text-emerald-300">
                  {t('transactions.receipt', 'Receipt')}
                </span>
              </div>
              {formatTransactionDetails(successMessage.details, successMessage.isDeposit)}
            </div>
          ) : (
            <form 
              onSubmit={(e) => {
                console.log("Form onSubmit triggered!");
                handleSubmit(e);
              }} 
              className="flex flex-col space-y-4">
              <div className="mb-3">
                <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                  <button
                    type="button"
                    onClick={() => setTransactionType('deposit')}
                    className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                      transactionType === 'deposit'
                        ? 'bg-emerald-600 text-white dark:bg-dark-primary-500 dark:hover:bg-dark-primary-600'
                        : 'bg-gray-100 dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary'
                    }`}
                  >
                    {t('wallet.deposit', 'Deposit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTransactionType('withdrawal')}
                    className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                      transactionType === 'withdrawal'
                        ? 'bg-red-600 text-white dark:bg-red-500 dark:hover:bg-red-600'
                        : 'bg-gray-100 dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary'
                    }`}
                  >
                    {t('wallet.withdraw', 'Withdrawal')}
                  </button>
                </div>
              </div>

              {!selectedUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('transactions.searchUser', 'Search User')}</label>
                  <div className="relative mt-1">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onFocus={handleSearchFocus}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder={t('transactions.searchPlaceholder', 'Search by name or username')}
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-300" aria-hidden="true" />
                    </div>
                    {filteredUsers.length > 0 && !selectedUser && (
                      <ul className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {filteredUsers.map(user => (
                          <li
                            key={user.id}
                            onClick={() => handleUserSelect(user)}
                            className="cursor-pointer select-none relative py-2 px-4 hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <div className="flex items-center gap-3">
                              {React.createElement(getUserIcon(user.type), {
                                className: `h-5 w-5 ${getUserStyle(user.type)}`
                              })}
                              <div className="flex flex-col min-w-0 flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-gray-900 dark:text-gray-100">{user.name}</span>
                                  <span className="text-sm text-emerald-600 dark:text-emerald-400">
                                    {formatBalance(user.balances?.[currency], currency)}
                                  </span>
                                </div>
                                <span className="text-sm text-gray-500 dark:text-gray-400">@{user.username}</span>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {selectedUser && (
                <div className={`p-3 rounded-lg ${getTransactionBgColor()} transition-colors`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {React.createElement(getUserIcon(selectedUser.type), {
                        className: `h-6 w-6 ${getUserStyle(selectedUser.type)}`
                      })}
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{selectedUser.name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">@{selectedUser.username}</span>
                      </div>
                    </div>
                    <button 
                      type="button"
                      onClick={handleRemoveUser}
                      className="p-1 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title={t('transactions.clearUser', 'Clear user')}
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('transactions.currentBalance', 'Current Balance')}:</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-semibold ${getTransactionColor()}`}>
                        {formatBalance(selectedUser.balances?.[currency] || userWalletBalances[currency], currency)}
                      </span>
                      {isRefreshing && (
                        <div className="animate-spin h-4 w-4 text-gray-500 dark:text-gray-400">
                          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <div className="w-20">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('wallet.currency', 'Currency')}</label>
                  <div className="mt-1 relative">
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="block w-full pl-3 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 appearance-none"
                      style={{ backgroundImage: 'none' }}
                    >
                      {availableCurrencies.length > 0 ? (
                        availableCurrencies.map(currency => (
                          <option key={currency} value={currency}>{currency}</option>
                        ))
                      ) : (
                        <option value="">{t('wallet.noCurrencies', 'No currencies available')}</option>
                      )}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('transactions.amount', 'Amount')}</label>
                  <div className="mt-1">
                    <input
                      type="text"
                      value={amount}
                      onChange={handleAmountChange}
                      onBlur={handleAmountBlur}
                      className="block w-full pl-3 pr-12 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setShowNotes(!showNotes)}
                  className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <svg
                    className={`w-4 h-4 transition-transform ${showNotes ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span>{t('transactions.notes', 'Notes')}</span>
                </button>
                {showNotes && (
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-2 block w-full pl-3 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder={t('transactions.notesPlaceholder', 'Add any notes about this transaction')}
                    rows="2"
                  />
                )}
              </div>

              {isCurrentUserAdmin && !isBreakglassActive && (
                <div className="flex items-center gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <input
                    type="checkbox"
                    id="testTransaction"
                    checked={isTestTransaction}
                    onChange={(e) => setIsTestTransaction(e.target.checked)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <label
                    htmlFor="testTransaction"
                    className="text-sm font-medium text-purple-700 dark:text-purple-300 cursor-pointer select-none"
                  >
                    {t('transactions.testTransaction', 'Test Transaction (Admin Only)')}
                  </label>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || !selectedUser || !isAmountValid() || !currency}
                  onClick={(e) => {
                    console.log("Button clicked!");
                    logSelectedUser();
                    console.log("Amount valid:", isAmountValid());
                    console.log("Amount:", amount);
                    console.log("Currency:", currency);
                  }}
                  style={{
                    // Make disabled state very obvious
                    opacity: (isSubmitting || !selectedUser || !isAmountValid() || !currency) ? 0.5 : 1,
                    cursor: (isSubmitting || !selectedUser || !isAmountValid() || !currency) ? 'not-allowed' : 'pointer',
                    pointerEvents: 'auto' // Ensure we can click for debugging
                  }}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors ${
                    transactionType === 'deposit'
                      ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500 dark:bg-dark-primary-500 dark:hover:bg-dark-primary-600 dark:focus:ring-dark-primary-500'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500 dark:bg-red-500 dark:hover:bg-red-600 dark:focus:ring-red-500'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('common.processing', 'Processing...')}
                    </span>
                  ) : (
                    <span>{transactionType === 'deposit' ? t('wallet.deposit', 'Deposit') : t('wallet.withdraw', 'Withdraw')}</span>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionModal; 